import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import ViewCircleIcon from '@assets/icons/view-circle.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_IMAGE_SIZE,
  ItemDetailsImageList,
} from '@/components/orders/item-details-image-list';
import { EmptyState } from '@/components/empty-state';
import { ExpandingTypography } from '@usepepper/ui/atoms/expanding-typography';
import { fennelTrack } from '@/analytics/fennel/hook';
import { ItemPurchaseHistoryList } from './item-purchase-history-list';
import { Platform, ScrollView, View } from 'react-native';
import { RelatedVariantPacksList } from './related-variant-packs-list';
import { TrackableComponent } from '@/enums';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useItemPurchaseHistory } from '@/hooks/items/use-item-purchase-history';
import {
  useRelatedVariantPacks,
  useVariantPack,
} from '@/hooks/items/use-variant-pack';
import { useRestaurantScopedFulfillment } from '@/hooks/use-selected-fulfillment';
import { useUserContext } from '@/contexts/user-context';

interface Props {
  onPressVariantPack?: (variantPackUUID: string) => void;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export const ItemDetails: React.FC<Props> = ({
  restaurantUUID,
  supplierUUID,
  variantPackUUID,
  onPressVariantPack,
}) => {
  const [styles, theme] = useStyles();

  const { restaurantUUIDToRelationshipMap } = useUserContext();
  const restaurant = restaurantUUIDToRelationshipMap[restaurantUUID];

  const { selectedFulfillment } = useRestaurantScopedFulfillment({
    restaurantUUID,
  });

  const {
    description,
    displayItemCode,
    itemModifiersEnabled,
    loading: variantPackLoading,
    name,
    photoURLs,
    variantPack,
  } = useVariantPack(
    variantPackUUID,
    restaurant,
    i18n.t('item_details.variant_pack.error.message'),
  );
  const { relatedVariantPacks, loading: relatedVariantPacksLoading } =
    useRelatedVariantPacks({
      displayOptions: {
        displayItemCode,
        itemModifiersEnabled,
      },
      externalItemId: variantPack?.variant_pack?.external_item_id ?? undefined,
      itemUUID: variantPack?.variant_pack?.item.uuid ?? undefined,
      nextFulfillmentDate: selectedFulfillment?.date ?? null,
      onErrorMessage: i18n.t(
        'item_details.related_variant_packs.error.message',
      ),
      restaurantUUID,
      skip: variantPackLoading || !selectedFulfillment,
      supplierUUID,
      variantPackUUID,
    });
  const { itemPurchaseHistoryData, loading: itemPurchaseHistoryLoading } =
    useItemPurchaseHistory(
      restaurant.chatUUIDs[0],
      variantPack?.variant_pack?.external_item_id ?? undefined,
      variantPack?.variant_pack?.item.uuid ?? undefined,
    );

  const trackItemPurchaseHistoryPress = useCallback(
    (open: boolean) => {
      fennelTrack(AnalyticEvent.ITEM_PURCHASE_HISTORY_PRESS, {
        componentName: TrackableComponent.ITEM_DETAILS,
        externalItemID:
          variantPack?.variant_pack?.external_item_id ?? undefined,
        itemUUID: variantPack?.variant_pack?.item.uuid ?? undefined,
        open,
        restaurantUUID,
        supplierUUID,
        variantPackUUID,
      });
    },
    [restaurantUUID, supplierUUID, variantPack, variantPackUUID],
  );

  const imageAltText = [name, variantPack?.variant_pack?.external_item_id]
    .filter(Boolean)
    .join('-');

  // Loading State
  if (!variantPack && variantPackLoading) {
    return (
      <View
        accessibilityState={{
          busy: true,
        }}
        style={styles.loadingContainer}
      >
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Empty State
  if (!variantPack) {
    return (
      <EmptyState
        description={i18n.t('item_details.no_variant_pack_found.description')}
        icon={<ViewCircleIcon stroke={theme.sk.color.icon.secondary} />}
        title={i18n.t('item_details.no_variant_pack_found.title')}
      />
    );
  }

  // TODO(DSR-3052): display promotional pricing
  // TODO(DSR-3055): display PAR and MOQ
  // TODO(DSR-3057): add favourite icon and logic
  return (
    <ScrollView
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.itemDetails}>
        {photoURLs && photoURLs.length > 0 ? (
          <ItemDetailsImageList
            alt={imageAltText}
            imageURLs={photoURLs}
            size={DEFAULT_IMAGE_SIZE}
          />
        ) : null}
        <View collapsable={false} style={styles.header}>
          <Typography variant="heading-xl">{name}</Typography>
          {Platform.OS === 'web' ? (
            <Typography variant="paragraph-base">{description}</Typography>
          ) : (
            <ExpandingTypography
              collapsedCopy={i18n.t('read_more')}
              expandedCopy={i18n.t('read_less')}
              numberOfLines={2}
              variant="paragraph-base"
            >
              {description}
            </ExpandingTypography>
          )}
        </View>
      </View>
      <RelatedVariantPacksList
        loading={
          variantPackLoading ||
          relatedVariantPacksLoading ||
          // HACK: prevent empty state from showing between the time selectedFulfillment data is loaded but not set
          !selectedFulfillment
        }
        onPressVariantPack={onPressVariantPack}
        variantPacks={relatedVariantPacks}
      />
      <ItemPurchaseHistoryList
        itemPurchaseHistoryData={itemPurchaseHistoryData}
        loading={itemPurchaseHistoryLoading}
        onPress={trackItemPurchaseHistoryPress}
      />
    </ScrollView>
  );
};

const useStyles = createStyles(() => ({
  container: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    paddingBottom: 24,
    paddingHorizontal: 16,
  },
  header: {
    gap: 4,
  },
  itemDetails: {
    gap: 24,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
}));
